// EditorManager: Monaco Editor wrapper
import { EditorConfig } from './types';

declare global {
  interface Window {
    monaco: any;
  }
}

export class EditorManager {
  editor: any = null;
  private onContentChangeCallback?: (content: string) => void;
  private onPositionChangeCallback?: (line: number, column: number) => void;

  async init(containerElement: HTMLElement, config: EditorConfig): Promise<void> {

    // Load Monaco from CDN if not already loaded
    if (!window.monaco) {
      await this.loadMonaco();
    }

    // Create editor instance
    this.editor = window.monaco.editor.create(containerElement, {
      value: '',
      language: 'plaintext',
      theme: config.theme,
      fontSize: config.fontSize,
      tabSize: config.tabSize,
      insertSpaces: config.insertSpaces,
      lineNumbers: config.lineNumbers,
      minimap: config.minimap,
      wordWrap: config.wordWrap,
      automaticLayout: true,
    });

    // Listen to content changes
    this.editor.onDidChangeModelContent(() => {
      if (this.onContentChangeCallback) {
        this.onContentChangeCallback(this.getContent());
      }
    });

    // Listen to cursor position changes
    this.editor.onDidChangeCursorPosition((e: any) => {
      if (this.onPositionChangeCallback) {
        this.onPositionChangeCallback(e.position.lineNumber, e.position.column);
      }
    });
  }

  private async loadMonaco(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Load Monaco loader
      const loaderScript = document.createElement('script');
      loaderScript.src = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs/loader.js';
      loaderScript.onload = () => {
        // Configure Monaco loader
        (window as any).require.config({
          paths: { vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs' },
        });
        (window as any).require(['vs/editor/editor.main'], () => {
          resolve();
        });
      };
      loaderScript.onerror = () => reject(new Error('Failed to load Monaco Editor'));
      document.head.appendChild(loaderScript);
    });
  }

  setContent(content: string, language: string): void {
    if (!this.editor) return;
    const model = this.editor.getModel();
    if (model) {
      window.monaco.editor.setModelLanguage(model, language);
      model.setValue(content);
    }
  }

  getContent(): string {
    return this.editor?.getValue() || '';
  }

  setLanguage(language: string): void {
    if (!this.editor) return;
    const model = this.editor.getModel();
    if (model) {
      window.monaco.editor.setModelLanguage(model, language);
    }
  }

  updateConfig(config: Partial<EditorConfig>): void {
    if (!this.editor) return;
    this.editor.updateOptions(config);
  }

  onContentChange(callback: (content: string) => void): void {
    this.onContentChangeCallback = callback;
  }

  onPositionChange(callback: (line: number, column: number) => void): void {
    this.onPositionChangeCallback = callback;
  }

  focus(): void {
    this.editor?.focus();
  }

  dispose(): void {
    this.editor?.dispose();
    this.editor = null;
  }
}
